<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          :color="quotation.converted_invoice_id ? 'grey' : 'teal'"
          v-bind="attrs"
          v-on="on"
          @click="open"
        >
          <v-icon>mdi-file-document-arrow-right</v-icon>
        </v-btn>
      </template>
      <span>{{
        quotation.converted_invoice_id
          ? `Invoiced (#${quotation.converted_invoice_id}) — convert again`
          : "Convert to Invoice"
      }}</span>
    </v-tooltip>

    <v-dialog v-model="dialog" persistent max-width="1000px">
      <v-card>
        <v-card-title>
          Convert Quotation #{{ quotation.quote_number }} to Invoice
          <v-spacer></v-spacer>
          <v-chip v-if="vatInclusive" small color="orange" dark>
            VAT Inclusive (12%)
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="quotation.converted_invoice_id"
            type="warning"
            dense
            outlined
            class="mb-4"
          >
            This quotation was already converted to Invoice #{{
              quotation.converted_invoice_id
            }}. Converting again creates another invoice and double-books the
            revenue.
          </v-alert>

          <!-- CUSTOMER -->
          <h3 class="text-h6 mb-2">Customer</h3>
          <v-row dense>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="selectedCustomer"
                :items="customers"
                item-text="customer_name"
                label="Select Customer"
                prepend-inner-icon="mdi-account-search"
                outlined
                dense
                return-object
                :search-input.sync="customerSearch"
                hide-details
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn
                block
                color="primary"
                outlined
                @click="showQuickCreate = !showQuickCreate"
              >
                <v-icon class="mr-1">mdi-account-plus</v-icon>
                {{ showQuickCreate ? "Hide" : "Quick Create" }}
              </v-btn>
            </v-col>
          </v-row>
          <v-card v-if="showQuickCreate" outlined class="pa-3 mt-2 mb-2">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Customer Name"
                  v-model="quickCreate.customer_name"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Contact Number"
                  v-model="quickCreate.customer_contact_no"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Municipality / Address"
                  v-model="quickCreate.customer_municipality"
                  dense
                  outlined
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-checkbox
                  label="Government"
                  v-model="quickCreate.is_government"
                  dense
                  hide-details
                  class="mt-1"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  block
                  color="success"
                  :loading="creatingCustomer"
                  @click="createCustomer"
                >
                  Create & Select
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <!-- LINES -->
          <h3 class="text-h6 mt-4 mb-2">
            Invoice Lines
            <span class="caption grey--text"
              >(map each quote item to a service or inventory item)</span
            >
          </h3>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Quote Item</th>
                <th style="width: 90px">Qty</th>
                <th style="width: 130px">Unit Price</th>
                <th style="width: 280px">Map To</th>
                <th class="text-right">Line Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td>
                  {{ row.item_name }}
                  <v-chip v-if="row.bundle_name" x-small class="ml-1" outlined>
                    {{ row.bundle_name }}
                  </v-chip>
                </td>
                <td>
                  <v-text-field
                    v-model="row.quantity"
                    type="number"
                    min="1"
                    dense
                    hide-details
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="row.price"
                    type="number"
                    min="0"
                    dense
                    hide-details
                  ></v-text-field>
                </td>
                <td>
                  <v-autocomplete
                    v-model="row.mapping"
                    :items="mappingOptions"
                    item-text="text"
                    dense
                    hide-details
                    return-object
                    placeholder="Select service or item"
                  ></v-autocomplete>
                </td>
                <td class="text-right">
                  {{ formatCurrency(lineTotal(row)) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="d-flex justify-end mt-3">
            <h3 class="text-h6">
              Total: {{ formatCurrency(grandTotal) }}
              <span v-if="vatInclusive" class="caption grey--text">
                (incl. 12% VAT)</span
              >
            </h3>
          </div>
        </v-card-text>
        <v-card-actions>
          <p class="caption ml-2">convertToInvoiceLauncher.vue</p>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!canContinue" @click="proceed">
            Continue to Invoice
            <v-icon class="ml-1">mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  props: ["quotation"],
  data() {
    return {
      dialog: false,
      customers: [],
      customerSearch: "",
      selectedCustomer: null,
      showQuickCreate: false,
      creatingCustomer: false,
      quickCreate: {},
      rows: [],
    };
  },
  computed: {
    ...mapGetters({
      storeCustomers: "customer/customers",
      items: "item/items",
      services: "service/services",
    }),
    vatInclusive() {
      // walk-in VAT quotations carry their 12% into the invoice as
      // VAT-inclusive prices so the books capture the same VAT Payable.
      return (
        (this.quotation.quotation_mode || "walk_in") === "walk_in" &&
        !!this.quotation.is_vat_purchase
      );
    },
    mappingOptions() {
      const serviceOpts = this.services.map((s) => ({
        text: `[Service] ${s.service_name}`,
        kind: "service",
        ref: s,
      }));
      // Serialized items ARE selectable — the invoice card (openWithQuote) shows
      // a per-row serial Pick button and blocks submit until serials are chosen,
      // so conversion just hands off the serial selection to that step.
      const itemOpts = this.items.map((i) => ({
        text: `[Item] ${i.item_name} (stock: ${i.quantity})${
          i.is_serialized ? " • serialized" : ""
        }`,
        kind: "item",
        ref: i,
      }));
      return [...serviceOpts, ...itemOpts];
    },
    grandTotal() {
      return this.rows.reduce((sum, r) => sum + this.lineTotal(r), 0);
    },
    canContinue() {
      return (
        this.selectedCustomer &&
        this.selectedCustomer.id &&
        this.rows.length > 0 &&
        this.rows.every(
          (r) => r.mapping && parseInt(r.quantity) > 0 && r.price !== ""
        )
      );
    },
  },
  methods: {
    async open() {
      if (this.quotation.converted_invoice_id) {
        const proceed = await this.$confirm({
          title: "Already Converted",
          message: `Quotation #${this.quotation.quote_number} was already converted to Invoice #${this.quotation.converted_invoice_id}. Converting again will double-book the revenue. Continue anyway?`,
          color: "warning",
          icon: "mdi-alert",
        });
        if (!proceed) return;
      }
      await this.ensureDataLoaded();
      this.prepopulate();
      this.dialog = true;
    },
    async ensureDataLoaded() {
      const jobs = [];
      if (!this.storeCustomers.length)
        jobs.push(this.$store.dispatch("customer/get_customers"));
      if (!this.items.length) jobs.push(this.$store.dispatch("item/get_items"));
      if (!this.services.length)
        jobs.push(this.$store.dispatch("service/get_services"));
      await Promise.all(jobs);
      this.customers = this.storeCustomers;
    },
    prepopulate() {
      // customer: exact-name match from the quotation's free-text name
      const name = (this.quotation.customer_name || "").trim().toLowerCase();
      this.selectedCustomer =
        this.customers.find(
          (c) => (c.customer_name || "").trim().toLowerCase() === name
        ) || null;
      this.customerSearch = this.quotation.customer_name || "";
      this.quickCreate = {
        customer_name:
          this.quotation.customer_name || this.quotation.company_name || "",
        customer_contact_no: this.quotation.phone_number || "",
        customer_municipality: this.quotation.address || "",
        // customers.tin is NOT NULL; the Add Customer modal uses this default
        tin: "000-000-000",
        is_government:
          (this.quotation.quotation_mode || "walk_in") === "government" ? 1 : 0,
      };
      const priceFactor = this.vatInclusive ? 1.12 : 1;
      this.rows = (this.quotation.quote_items || [])
        .filter((qi) => qi.item_name && qi.item_name.trim() !== "")
        .map((qi) => ({
          item_name: qi.item_name,
          bundle_name: qi.bundle_name || null,
          quantity: qi.quantity || 1,
          price: (
            (parseFloat(qi.final_quote_price_per_unit) || 0) * priceFactor
          ).toFixed(2),
          mapping: this.autoMatch(qi),
        }));
    },
    // Score-based matching against services and inventory items: exact name
    // wins outright; a partial name hit needs corroboration from the supplier
    // or a close price (quote price vs selling price, supplier cost vs unit
    // cost) so items procured through inventory still resolve correctly.
    autoMatch(qi) {
      const needle = (qi.item_name || "").trim().toLowerCase();
      if (!needle) return null;
      const quotePrice = parseFloat(qi.final_quote_price_per_unit) || 0;
      const costPrice = parseFloat(qi.final_supplier_price) || 0;
      const supplier = (qi.supplier || "").trim().toLowerCase();
      const close = (a, b) => b > 0 && Math.abs(a - b) / b <= 0.05;
      const nameScore = (cand) => {
        const n = (cand || "").trim().toLowerCase();
        if (!n) return 0;
        if (n === needle) return 100;
        if (
          needle.length >= 4 &&
          n.length >= 4 &&
          (n.includes(needle) || needle.includes(n))
        )
          return 40;
        return 0;
      };

      const candidates = [];
      this.services.forEach((s) => {
        const score =
          nameScore(s.service_name) +
          (close(parseFloat(s.service_amount) || 0, quotePrice) ? 15 : 0);
        if (score > 0)
          candidates.push({
            score,
            text: `[Service] ${s.service_name}`,
            kind: "service",
            ref: s,
          });
      });
      this.items.forEach((i) => {
        const score =
          nameScore(i.item_name) +
          (supplier && (i.supplier || "").trim().toLowerCase() === supplier
            ? 20
            : 0) +
          (close(parseFloat(i.selling_price) || 0, quotePrice) ? 15 : 0) +
          (close(parseFloat(i.unit_price) || 0, costPrice) ? 10 : 0);
        if (score > 0)
          candidates.push({
            score,
            text: `[Item] ${i.item_name} (stock: ${i.quantity})${
              i.is_serialized ? " • serialized" : ""
            }`,
            kind: "item",
            ref: i,
          });
      });
      if (!candidates.length) return null;
      candidates.sort((a, b) => b.score - a.score);
      const best = candidates[0];
      // exact name (100) always maps; a partial name (40) must be backed by
      // supplier (+20) or price (+15/+10) to reach the threshold.
      return best.score >= 55 ? best : null;
    },
    lineTotal(row) {
      return (parseFloat(row.price) || 0) * (parseInt(row.quantity) || 0);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0);
    },
    async createCustomer() {
      if (!this.quickCreate.customer_name) {
        this.$alert({
          title: "Validation",
          message: "Customer name is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.creatingCustomer = true;
      try {
        await this.$store.dispatch("customer/add_customer", this.quickCreate);
        // add_customer doesn't return the record; resolve the newest match
        // by name (the server uppercases customer_name).
        const needle = (this.quickCreate.customer_name || "")
          .trim()
          .toLowerCase();
        const matches = this.storeCustomers.filter(
          (c) => (c.customer_name || "").trim().toLowerCase() === needle
        );
        this.customers = this.storeCustomers;
        this.selectedCustomer = matches.sort((a, b) => b.id - a.id)[0] || null;
        this.showQuickCreate = false;
      } catch (e) {
        this.$alert({
          title: "Error",
          message: String(e),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.creatingCustomer = false;
    },
    proceed() {
      const rows = this.rows.map((r) => ({
        ...r.mapping.ref,
        type: r.mapping.kind,
        quantity: parseInt(r.quantity),
        custom_price: parseFloat(r.price),
        remarks: r.bundle_name || undefined,
      }));
      this.$emit("convert", {
        quotation: this.quotation,
        customer: this.selectedCustomer,
        rows,
        vatInclusive: this.vatInclusive,
      });
      this.dialog = false;
    },
  },
};
</script>
